// Industry hierarchical select for Alpine.js (checkbox tree).
//
// Requirements:
// - Clicking the select area opens a dropdown that initially shows only root industries.
// - Clicking a root industry label toggles showing all descendants (hierarchical).
// - Checkboxes select items; selecting a parent auto-selects all descendants.
//
// options: [{ id: number, label: string, ancestry?: string|null }]
window.industryTreeSelect = function({
  options,
  selectedIds,
  inputName,
  placeholder,
  // expandMode:
  // - 'root_all'  : root toggle shows all descendants (current behavior)
  // - 'two_level' : root toggle shows only depth=1; depth=1 toggle shows all deeper descendants
  expandMode
} = {}) {
  const initial = Array.isArray(selectedIds) ? selectedIds : []

  const parseIntSafe = (v) => {
    const n = parseInt(v, 10)
    return Number.isNaN(n) ? null : n
  }

  const parentIdFromAncestry = (ancestry) => {
    if (!ancestry) return null
    const parts = String(ancestry).split('/').filter(Boolean)
    if (parts.length === 0) return null
    return parseIntSafe(parts[parts.length - 1])
  }

  const depthFromAncestry = (ancestry) => {
    if (!ancestry) return 0
    const parts = String(ancestry).split('/').filter(Boolean)
    return parts.length
  }

  return {
    open: false,
    options: options || [],
    expandMode: expandMode || 'root_all',
    expandedIds: [],
    selectedIds: initial.map((v) => parseInt(v, 10)).filter((v) => !Number.isNaN(v)),
    inputName: inputName || 'ids[]',
    placeholder: placeholder || '',

    init() {
      this.selectedIds = Array.from(new Set(this.selectedIds))
      this.expandedIds = []
    },

    close() {
      this.open = false
      this.expandedIds = []
    },

    get _nodes() {
      return (this.options || []).map((o) => ({
        id: o.id,
        label: o.label,
        ancestry: o.ancestry || null,
        parentId: parentIdFromAncestry(o.ancestry),
        depth: depthFromAncestry(o.ancestry)
      }))
    },

    get _childrenMap() {
      const map = new Map()
      const ensure = (k) => {
        if (!map.has(k)) map.set(k, [])
        return map.get(k)
      }
      for (const n of this._nodes) {
        ensure(n.parentId).push(n)
      }
      // stable ordering (label asc, id asc)
      for (const [k, arr] of map.entries()) {
        arr.sort((a, b) => (a.label || '').localeCompare(b.label || '') || (a.id - b.id))
        map.set(k, arr)
      }
      return map
    },

    _childrenOf(parentId) {
      return this._childrenMap.get(parentId) || []
    },

    _collectSubtree(parentId, depthBase) {
      const out = []
      const children = this._childrenOf(parentId)
      for (const c of children) {
        out.push({ ...c, depth: depthBase })
        out.push(...this._collectSubtree(c.id, depthBase + 1))
      }
      return out
    },

    get roots() {
      return this._childrenOf(null).map((n) => ({ ...n, depth: 0 }))
    },

    get displayedOptions() {
      const expanded = new Set(this.expandedIds)
      const out = []
      for (const r of this.roots) {
        out.push(r)
        if (!expanded.has(r.id)) continue

        if (this.expandMode === 'two_level') {
          // root expands to depth=1 only; depth=1 expands its whole subtree
          const depth1Children = this._childrenOf(r.id).map((n) => ({ ...n, depth: 1 }))
          for (const c of depth1Children) {
            out.push(c)
            if (expanded.has(c.id)) {
              out.push(...this._collectSubtree(c.id, 2))
            }
          }
        } else {
          // root_all (default): root expands to all descendants
          out.push(...this._collectSubtree(r.id, 1))
        }
      }
      return out
    },

    depthOf(id) {
      const n = this._nodes.find((x) => x.id === id)
      return n ? n.depth : null
    },

    hasChildren(id) {
      return this._childrenOf(id).length > 0
    },

    isExpandableDepth(depth) {
      if (this.expandMode === 'two_level') return depth === 0 || depth === 1
      return depth === 0
    },

    toggleExpand(id) {
      const depth = this.depthOf(id)
      if (depth == null) return
      if (!this.isExpandableDepth(depth)) return

      const expanded = new Set(this.expandedIds)
      if (expanded.has(id)) {
        expanded.delete(id)
        // if collapsing root in two_level mode, also collapse any expanded descendants (depth=1 toggles)
        if (this.expandMode === 'two_level' && depth === 0) {
          for (const did of this._descendantIds(id)) expanded.delete(did)
        }
      } else {
        expanded.add(id)
      }
      this.expandedIds = Array.from(expanded)
    },

    isSelected(id) {
      return this.selectedIds.includes(id)
    },

    _descendantIds(id) {
      const out = []
      const stack = [id]
      while (stack.length > 0) {
        const cur = stack.pop()
        const kids = this._childrenOf(cur)
        for (const k of kids) {
          out.push(k.id)
          stack.push(k.id)
        }
      }
      return out
    },

    toggleWithDescendants(id) {
      const descendants = this._descendantIds(id)
      const next = new Set(this.selectedIds)
      const targetIds = [id, ...descendants]

      if (this.isSelected(id)) {
        for (const tid of targetIds) next.delete(tid)
      } else {
        for (const tid of targetIds) next.add(tid)
      }

      this.selectedIds = Array.from(next)
    },

    get selectedCount() {
      return this.selectedIds.length
    }
  }
}

window.industryTreeSelectFromDataset = function(el) {
  const parseJson = (v, fallback) => {
    if (!v) return fallback
    try {
      return JSON.parse(v)
    } catch (_e) {
      return fallback
    }
  }

  const options = parseJson(el.dataset.options, [])
  const selected = parseJson(el.dataset.selected, [])

  return window.industryTreeSelect({
    options,
    selectedIds: selected,
    inputName: el.dataset.inputName,
    placeholder: el.dataset.placeholder,
    expandMode: el.dataset.expandMode
  })
}


